import { WeekPicker } from 'app/components/common/DatePickers';
import { AgenturNameCell } from 'app/components/shared/AgenturName.cell';
import { Table } from 'app/components/table/table';
import { AggregrationType, ITableColumn, TableModel, TableRow } from 'app/components/table/table.model';
import { useStore } from 'app/context';
import { AkumaAktionenVM } from 'app/stores/ui/agt.akumaaktionen.ui.store';
import { formatPercentTwoDig } from 'app/utils';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { useEffect, useMemo } from 'react';
import { useHistoryWithQuery } from 'app/hooks/useHistoryWithQuery';
import _ from 'lodash';
import { schemaAkumaAktionenOverview } from 'app/stores/agt.akumaaktionen.store';
import { Modal } from 'app/components/Modal';
import { Button } from 'app/components/common/Button';
import { Icon } from 'app/components/common/Icon';

export const AkumaAktionenOptions = observer(() => {
	const { agtAkumaAktionenUiStore } = useStore();

	const onBmChange = (value: number) => {
		runInAction(() => {
			agtAkumaAktionenUiStore.setKw(value);
		});
	};

	const bmOptions = agtAkumaAktionenUiStore.availableKws.map((bm) => ({
		value: bm,
		label: `${Math.floor(bm / 100)}/${bm % 100}`,
	}));

	return <WeekPicker value={agtAkumaAktionenUiStore.selectedKw} opts={bmOptions} onChange={onBmChange} emptyText="Alle Monate" queryParam="bm" />;
});

export const AkumaAktionenOverviewListe = observer(() => {
	const { berichteUiiStore, agtAkumaAktionenUiStore, agenturListUiStore, uiStore } = useStore();
	const history = useHistoryWithQuery();

	const tm = useMemo(() => {
		const tm = new TableModel<AkumaAktionenVM, number>();
		const cols: ITableColumn<AkumaAktionenVM, number>[] = [];

		cols.push({
			label: 'Agentur',
			path: 'agt.name',
			sortBy: 'agt.name',
			render: AgenturNameCell,
		});

		schemaAkumaAktionenOverview.forEach((s) => {
			cols.push({
				label: s.label + ' (Anzahl)',
				path: 'overviewItem.' + s.name + 'CustumerCount',
				agg: [
					{
						path: 'overviewItem.' + s.name + 'CustumerCount',
						aggType: AggregrationType.sum,
					},
				],
			});
			cols.push({
				label: s.label + ' initiale Bearbeitung',
				path: 'overviewItem.' + s.name + 'View', // bearbeitet
				agg: [
					{
						path: 'overviewItem.' + s.name + 'View',
						aggType: AggregrationType.sum,
					},
				],
			});
			cols.push({
				label: s.label + ' fortgeführete Bearbeitung',
				path: 'overviewItem.' + s.name + 'AgencyCount', // bearbeitet
				agg: [
					{
						path: 'overviewItem.' + s.name + 'AgencyCount',
						aggType: AggregrationType.sum,
					},
				],
			});

			cols.push({
				label: s.label + ' Quote',
				path: 'overviewItem.' + s.name + 'Quote',
				format: formatPercentTwoDig,
			});
		});

		tm.onRowClick = (row: TableRow<AkumaAktionenVM>) => {
			history.pushWithQuery({
				search: '?agtId=' + row.data.agt.agtId,
			});
		};

		tm.sortBy = 'agt.name';
		tm.setCols(cols);

		tm.idProperty = 'agt.agtId';
		tm.idType = 'number';

		return tm;
	}, []);

	useEffect(() => {
		// Load both base data and campaign count overview data
		const agtId = berichteUiiStore.selectedAgt?.agtId;
		Promise.all([agtAkumaAktionenUiStore.load(), agtAkumaAktionenUiStore.loadCampaignCountOverviewByBm(agtAkumaAktionenUiStore.selectedKw, agtId)]).then(() => {
			let items = agtAkumaAktionenUiStore.items;
			if (berichteUiiStore.selectedAgt) {
				const agtId = berichteUiiStore.selectedAgt.agtId;
				items = items.filter((i) => i.agt.agtId === agtId);
				items = _.sortBy(items, 'bm').reverse();
			} else {
				items = agtAkumaAktionenUiStore.applyFilter(items);
			}

			tm.setRowData(items);
			tm.sort();
		});
	}, [
		tm,
		berichteUiiStore.selectedAgt,
		agenturListUiStore.filterHasChanged,
		agenturListUiStore.currentFilter.filtersWithValue.length,
		agtAkumaAktionenUiStore.selectedKw,
		agtAkumaAktionenUiStore.items,
	]);

	return (
		<>
			<div className="table-container">
				<div className="grid is-col-auto align-center justify-start pad-1rem">
					<AkumaAktionenOptions />
					<Button className="button is-primary is-small is-inverted" onClick={() => uiStore.showModal('akumaAktionenLegend')}>
						<Icon iconClass="info-circle" />
						<span>Legende</span>
					</Button>
				</div>
				<div className="table-container">
					<Table stickyHeader={true} tm={tm} stickyFooter={true}></Table>
				</div>
			</div>
			<Modal modalId={'akumaAktionenLegend'} title={'Erläuterung der Dargestellten Kennzahlen'} onClose={() => uiStore.hideModal('akumaAktionenLegend')}>
				<div className="content">
					<ul>
						<li>
							<strong>Top-Aktionen:</strong> Aktionen die als Top-Aktionen markiert sind
						</li>
						<li>
							<strong>TOP Aktionen - initiale Bearbeitung:</strong> Die Potenzialliste einer Top-Aktion wurde in AKUMA Aktionen geöffnet (Kundenliste).
						</li>
						<li>
							<strong>Top-Aktionen - fortgeführte Bearbeitung:</strong> Nach der initialen Bearbeitung wurde eine Top-Aktion weiterbearbeitet, indem entweder für mind. 5 % des Aktionspotenzials Aufgaben in AMIS.NOW erstellt oder ein PDF-Export der Aktionsliste durchgeführt wurde. 5 % sind ein reiner Ambitionswert. Sie zeigen an, dass eine systematische Auseinandersetzung mit einer relevanten Anzahl an Kunden aus dem Aktionspotenzial stattgefunden hat und konkrete Maßnahmen eingeleitet wurden.
						</li>
						<li>
							<strong>Top-Aktionen Quote:</strong> Quote der bearbeiten Top-Aktionen im Verhältnis zur Gesamtanzahl aller Top-Aktionen. Mind. 75% der Top-Aktionen sollen bearbeitet
							werden (Ambitionsniveau)
						</li>
						<li>
							<strong>Aktionen Download:</strong> Export der PDF-Kundenliste zur Aktion hat stattgefunden
						</li>
						<li>
							<strong>Nicht bearbeitet:</strong> Aktionen, die noch nicht bearbeitet wurden
						</li>
						<li>
							<strong>Aktionen:</strong> alle weiteren Aktionen, die keine Top-Aktion sind
						</li>
						<li>
							<strong>Aktionen bearbeitet:</strong> weitere Aktionen (nicht Top-Aktionen) in denen zu mind. 5% des Aktionspotenzials Aufgaben in AMIS.NOW erstellt oder ein PDF-Export der
							Aktionsliste erfolgt ist
						</li>
						<li>
							<strong>Leben/ Sach/ Kranken/ Übergreifend:</strong> Aufteilung nach Versicherungsparten
						</li>
					</ul>
				</div>
			</Modal>
		</>
	);
});
